import React, { useEffect, useState } from "react";
import { Plus, Mail, Send, X, Copy, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function InvitationsPage() {
  const [w, setW] = useState(null);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: "save_the_date", title: "", message: "" });
  const [qrFor, setQrFor] = useState(null);

  async function load() {
    const ww = await api.get("/weddings/mine");
    setW(ww.data);
    const inv = await api.get(`/weddings/${ww.data.wedding_id}/invitations`);
    setList(inv.data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/weddings/${w.wedding_id}/invitations`, form);
      setForm({ type: "save_the_date", title: "", message: "" });
      setOpen(false);
      toast.success("Invitation created");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  function shareLink(token) {
    const url = `${window.location.origin}/site/${w.slug}?inv=${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  }

  return (
    <div data-testid="invitations-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Digital Invitations</p>
          <h1 className="font-serif font-light text-4xl">Send luxury invites in a tap</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="create-invitation-btn" className="btn-gold"><Plus className="w-4 h-4" /> New Invitation</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && <p className="col-span-full text-center text-[var(--muted)] py-16 bg-white border border-[var(--line)] rounded-2xl">No invitations yet. Create your first one.</p>}
        {list.map((inv) => (
          <div key={inv.invitation_id} className="bg-white border border-[var(--line)] rounded-2xl p-6">
            <div className="flex items-start justify-between mb-4">
              <Mail className="w-5 h-5 text-[var(--gold)]" />
              <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">{inv.type.replace(/_/g, " ")}</span>
            </div>
            <h3 className="font-serif text-xl mb-2">{inv.title}</h3>
            <p className="text-sm text-[var(--muted)] mb-6 line-clamp-3">{inv.message}</p>
            <div className="flex items-center gap-2 pt-4 border-t border-[var(--line)]">
              <button onClick={() => shareLink(inv.share_token)} className="text-xs flex items-center gap-1 text-[var(--gold)]"><Copy className="w-3 h-3" /> Copy Link</button>
              <button onClick={() => setQrFor(inv)} className="text-xs flex items-center gap-1 text-[var(--gold)] ml-auto"><QrCode className="w-3 h-3" /> QR Code</button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md">
            <div className="flex justify-between items-start mb-6">
              <h3 className="font-serif text-2xl">New Invitation</h3>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Type</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-luxe">
                  <option value="save_the_date">Save the Date</option>
                  <option value="wedding">Wedding Invite</option>
                  <option value="engagement">Engagement Party</option>
                  <option value="shower">Bridal Shower</option>
                  <option value="bachelor">Bachelor</option>
                  <option value="bachelorette">Bachelorette</option>
                  <option value="reception">Reception</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Title</label>
                <input data-testid="form-inv-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-luxe" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Message</label>
                <textarea data-testid="form-inv-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="input-luxe resize-none" />
              </div>
              <button data-testid="form-inv-submit" disabled={busy} type="submit" className="btn-gold w-full"><Send className="w-4 h-4" /> {busy ? "Creating…" : "Create"}</button>
            </form>
          </div>
        </div>
      )}

      {qrFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center">
            <h3 className="font-serif text-2xl mb-2">{qrFor.title}</h3>
            <p className="text-xs uppercase tracking-wider text-[var(--muted)] mb-6">{qrFor.type.replace(/_/g, " ")}</p>
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/qr?data=${encodeURIComponent(`${window.location.origin}/site/${w.slug}?inv=${qrFor.share_token}`)}`} alt="QR" className="mx-auto border border-[var(--line)] rounded-xl" />
            <button onClick={() => setQrFor(null)} className="btn-outline-gold mt-6">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
